// The word-cloud report-param grammar, shared by every block that draws a cloud.
//
// One param per cloud -- `wc<id>` -- holding a `!`-joined list of tokens. A token is a two-letter
// name and its value; the on/off word filters share a single grouped token. A named preset stands
// in for every field it owns, so what gets written down is only the fields the preset does not own
// and only where they differ from the defaults.
//
// Nothing here knows the field set. The sync descriptor names the fields, their tokens, their map
// tables and which of them a preset owns, so a host with its own descriptor drives the same
// grammar; the preset tuple, the map tables and the gradients come off the same descriptor.

import { isEqual } from 'lodash'

import { getColorThemes, getReportParams } from '@aytm/app/stats/lib/stat-page'
import {
  RANGE_START_TO,
  scaleRangeStart
} from '@aytm/app/stats/page/question-views/components/word-cloud/utils/applyPreset'

// What a preset tuple carries, in order.
const [SCALE_FUNCTION, FONT_FAMILY, ANGLES, COLORS, BACKGROUND, COLOR_STYLE, SCALE_RANGE, MAX_WORDS, ORIENTATION] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8
]

const CUSTOM_PRESET = '-1'

const presetOwned = field => undefined === field.presetDependable || Boolean(field.presetDependable)
const questionOwned = field => false === field.presetDependable

export const wcParamName = id => `wc${id}`

const mergeDefaults = (lib, modelDefaults) => ({ ...lib.defaults, ...modelDefaults })

// --- the preset ------------------------------------------------------------------------

const presetColors = ({ preset, lib, colorThemes }) => {
  const colors = preset[1][COLORS]

  if (Array.isArray(colors)) return colors.slice()
  if ('gradient' === preset[1][COLOR_STYLE]) return lib.gradients[colors - 1][0].slice()

  return colorThemes[colors - 1].slice(0, 7)
}

// A preset owns the look of the cloud; the fields scaled to the corpus are the two the host has to
// rebuild whenever the corpus moves.
const applyPreset = ({ config, preset, lib, rejects, corpusSize, rangeStartTo, colorThemes }) => {
  if (undefined === rejects.scaleFunction) config.scaleFunction = preset[1][SCALE_FUNCTION]
  if (undefined === rejects.fontFamily) config.fontFamily = preset[1][FONT_FAMILY]
  if (undefined === rejects.angles) config.angles = preset[1][ANGLES].map(v => Number(v))

  if (undefined === rejects.colors) config.colors = presetColors({ preset, lib, colorThemes })

  if (undefined === rejects.colorStyle) {
    config.background = preset[1][BACKGROUND]
    config.colorStyle = preset[1][COLOR_STYLE]
  }

  if (null != preset[1][SCALE_RANGE] && undefined === rejects.scaleRange) {
    config.rangeStart = scaleRangeStart(corpusSize, rangeStartTo, preset[1][SCALE_RANGE][0])
    config.rangeEnd = preset[1][SCALE_RANGE][1]
  }

  if (null != preset[1][MAX_WORDS] && undefined === rejects.maxWords) {
    config.maxWords = Math.min(preset[1][MAX_WORDS], corpusSize)
  }

  if (null != preset[1][ORIENTATION] && undefined === rejects.orientation) {
    config.orientation = preset[1][ORIENTATION]
  }

  return config
}

// A preset id the descriptor no longer offers: the cloud settles on the id it was told to, and a
// cap left at zero is opened up to the whole corpus.
const applyOutdatedPreset = ({ config, currentPresetId, rejects, corpusSize, onOutdatedPreset }) => {
  config.currentPresetId = currentPresetId

  if (0 === config.maxWords && undefined === rejects.maxWords) {
    config.maxWords = corpusSize
    onOutdatedPreset?.()
  }

  return config
}

// --- importing an exported preset -------------------------------------------------------

const copyFromPreset = ({ config, preset, sync, rejects, accepts }) => {
  for (const [attr, field] of Object.entries(sync)) {
    if (!accepts(field)) continue
    if (undefined === preset[field.expName] || undefined !== rejects[field.expName]) continue

    const value = 'number' === typeof config[attr] ? Number(preset[field.expName]) : preset[field.expName]
    if (null != value) config[attr] = value
  }
}

/**
 * Applies an exported preset bag over a config: a named preset by id, or the fields themselves
 * when the bag is a custom one. The named case reproduces the look from the descriptor and leaves
 * the fields the question owns -- its mask, its blacklist, its active word -- alone.
 */
export const importWcConfig = ({ preset, config, lib, rejects = {}, corpusSize = 0, onOutdatedPreset } = {}) => {
  const id = preset.preset

  // the sentinel travels as a number in a saved custom preset and as a string in an exported bag
  if (null != id && CUSTOM_PRESET !== String(id)) {
    const named = lib.presetConfig(id)

    if (!named) return applyOutdatedPreset({ config, currentPresetId: id, rejects, corpusSize, onOutdatedPreset })

    applyPreset({
      config,
      preset: named,
      lib,
      rejects,
      corpusSize,
      rangeStartTo: RANGE_START_TO,
      colorThemes: getColorThemes() || []
    })

    config.currentPresetId = id

    return config
  }

  copyFromPreset({ config, preset, sync: lib.sync, rejects, accepts: presetOwned })
  copyFromPreset({ config, preset, sync: lib.sync, rejects, accepts: questionOwned })

  // the bag carried the fields themselves, so the cloud is the custom preset however the bag spelled it
  config.currentPresetId = CUSTOM_PRESET

  return config
}

// --- exporting a preset -----------------------------------------------------------------

/**
 * What one cloud hands another so it can be drawn the same way, keyed by exchange name.
 *
 * `full` carries everything. `preset` carries everything too but keys by field name, which is the
 * shape a saved preset is stored in. `custom` -- and any cloud already off the custom preset --
 * carries only the look, since the fields the question owns (its mask, its blacklist, its active
 * word) mean nothing to the cloud receiving it.
 */
export const exportWcConfig = ({ config = {}, lib, rejects = {}, expType = 'full' } = {}) => {
  const byFieldName = 'preset' === expType
  const currentPresetId = config.currentPresetId

  const bag = {}
  bag[byFieldName ? 'currentPresetId' : 'preset'] = ('custom' !== expType && currentPresetId) || -1

  for (const [attr, field] of Object.entries(lib.sync)) {
    if ('currentPresetId' === attr || undefined !== rejects[field.expName]) continue

    const carriesEverything = ['full', 'preset'].includes(expType)
    const carriesTheLook = ('custom' === expType || CUSTOM_PRESET === String(currentPresetId)) && presetOwned(field)

    if (!carriesEverything && !carriesTheLook) continue

    const value = config[attr]
    bag[byFieldName ? attr : field.expName] = 'array' === field.type ? structuredClone(value) : value
  }

  return bag
}

// --- reading the param ------------------------------------------------------------------

const findRow = (table, index, value) => table.find(row => row[index] === value)

const parseToken = ({ config, attr, field, data, lib }) => {
  const cast = value => ('number' === field.subtype ? Number(value) : value)

  if ('array' === field.type) {
    if (undefined !== field.mapTable && data.length <= 2) {
      const row = findRow(lib[field.mapTable], field.mapToIndex, data)
      if (row) config[attr] = cast(row[field.mapFromIndex])

      return
    }

    const values = data.split('~')
    config[attr] = (undefined !== field.prefix ? values.map(v => field.prefix + v) : values).map(v => cast(v))

    return
  }

  if (undefined !== field.mapTable) {
    const row = findRow(lib[field.mapTable], field.mapToIndex, data)
    if (row) config[attr] = row[field.mapFromIndex]

    return
  }

  if (undefined !== field.prefix) {
    config[attr] = field.prefix + data

    return
  }

  config[attr] = 'number' === field.type && !Number.isNaN(Number(data)) ? Number(data) : data
}

// A stored param always describes a custom cloud until a preset token says otherwise.
const parseSection = ({ section, lib }) => {
  const config = { currentPresetId: CUSTOM_PRESET }

  for (const token of section.split('!')) {
    const urlName = token.slice(0, 2)
    const data = token.slice(2)

    for (const [attr, field] of Object.entries(lib.sync)) {
      if (urlName !== field.filter) continue
      if (data.includes(field.filterOn)) config[attr] = 1
      if (data.includes(field.filterOff)) config[attr] = 0
    }

    for (const [attr, field] of Object.entries(lib.sync)) {
      if (field.urlName !== urlName) continue

      parseToken({ config, attr, field, data, lib })
      break
    }
  }

  return config
}

// The defaults the stored fields sit on. While a preset is named it answers for everything it
// owns, so only the question's own fields are seeded; a reject answers for its field outright.
const seedDefaults = ({ config, defaults, sync, rejects }) => {
  const seeded = {}

  for (const [key, value] of Object.entries(defaults)) {
    const field = sync[key]
    const rejected = undefined !== field && undefined !== rejects[field.expName]

    if (CUSTOM_PRESET !== config.currentPresetId && undefined !== field && (!questionOwned(field) || rejected)) continue

    if (rejected) {
      seeded[field.expName] = rejects[field.expName]
      continue
    }

    const stored = config[key]
    seeded[key] = stored && undefined === field ? stored : value
  }

  return { ...seeded, ...config }
}

/**
 * The config a cloud opens on: what the report param holds, on top of the defaults, with the named
 * preset it points at applied over both. `corpusSize` scales the preset's cap and font floor, so a
 * host has to resolve again whenever its corpus moves.
 *
 * A host that already holds the section says so: a write moves the page-model attribute first and
 * the params store only when the local changes resolve, so re-reading the store here would answer
 * with the section as it was before the write that prompted the resolve.
 */
export const resolveWcConfig = ({
  id,
  section,
  lib,
  modelDefaults,
  rejects = {},
  corpusSize = 0,
  onOutdatedPreset
} = {}) => {
  // null is a section the host cleared; only undefined means it never named one
  const source = undefined === section ? getReportParams()[wcParamName(id)] : section
  const stored = null != source ? parseSection({ section: source, lib }) : {}

  const names = Object.keys(stored).filter(key => undefined !== lib.sync[key] && !questionOwned(lib.sync[key]))
  if (names.length && !stored.currentPresetId) stored.currentPresetId = CUSTOM_PRESET

  const config = seedDefaults({ config: stored, defaults: mergeDefaults(lib, modelDefaults), sync: lib.sync, rejects })

  const currentPresetId = config.currentPresetId
  delete config.currentPresetId

  if (CUSTOM_PRESET === currentPresetId) {
    config.currentPresetId = CUSTOM_PRESET

    return config
  }

  const preset = lib.presetConfig(currentPresetId)
  if (!preset) {
    return applyOutdatedPreset({ config, currentPresetId: CUSTOM_PRESET, rejects, corpusSize, onOutdatedPreset })
  }

  applyPreset({
    config,
    preset,
    lib,
    rejects,
    corpusSize,
    rangeStartTo: RANGE_START_TO,
    colorThemes: getColorThemes() || []
  })

  config.currentPresetId = currentPresetId

  return config
}

// --- writing the param ------------------------------------------------------------------

const arrayToken = ({ value, field, lib }) => {
  if (undefined !== field.mapTable) {
    const row = lib[field.mapTable].find(r => r[field.mapFromIndex].join() === value.join())
    if (row) return row[field.mapToIndex]
  }

  const prefix = undefined !== field.prefix ? field.prefix : ''

  return value.map(v => String(v).slice(prefix.length)).join('~')
}

const scalarToken = ({ value, field, lib }) => {
  if (undefined !== field.mapTable) {
    const table = lib[field.mapTable]

    return (findRow(table, field.mapFromIndex, value) || table[table.length - 1])[field.mapToIndex]
  }

  const prefix = undefined !== field.prefix ? field.prefix : ''

  return String(value).slice(prefix.length)
}

/**
 * Serializes a config down to the param string: nothing a named preset already reproduces, nothing
 * that still matches the defaults, nothing a reject answers for. The on/off word filters collapse
 * into one grouped token, and the tokens are sorted so the same cloud always writes the same string.
 */
export const calcWcParamsString = ({ config, lib, modelDefaults, rejects = {} } = {}) => {
  const defaults = mergeDefaults(lib, modelDefaults)
  const presetExists = !lib.sync.currentPresetId.ignoreValues.includes(config.currentPresetId)

  const tokens = []
  const filters = {}

  for (const [attr, field] of Object.entries(lib.sync)) {
    if (presetExists && !questionOwned(field)) continue

    const value = config[attr]

    if (isEqual(value, defaults[attr]) || ('currentPresetId' === attr && !presetExists)) continue
    if (undefined !== rejects[field.expName]) continue

    if (undefined !== field.filter) {
      filters[field.filter] = (filters[field.filter] || '') + (0 === Number(value) ? field.filterOff : field.filterOn)
      continue
    }

    if (Array.isArray(value)) {
      tokens.push(field.urlName + arrayToken({ value, field, lib }))
      continue
    }

    if (undefined !== field.ignoreValues && field.ignoreValues.includes(value)) continue

    tokens.push(field.urlName + scalarToken({ value, field, lib }))
  }

  for (const [name, value] of Object.entries(filters)) tokens.push(name + value)

  return tokens.sort().join('!')
}

// A cloud that says nothing the defaults do not already say drops its param rather than storing an
// empty one.
export const calcWcParamsSection = ({ id, config, lib, modelDefaults, rejects } = {}) => {
  const value = calcWcParamsString({ config, lib, modelDefaults, rejects })

  return { [wcParamName(id)]: value.length ? value : null }
}
